import fetchWithAuth from "@/api/secureFetch";
import type { ContextMeshCategoryScores, ContextMeshTier } from "../meshStrength/contextMeshAnalysis";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export type ContextMeshAccessRole = "owner" | "editor" | "viewer";
export type ContextMeshAnalysisStatus = "pending" | "running" | "ready" | "failed" | "incomplete" | "unavailable";

export interface ContextMeshAnalysisSummary {
    status: ContextMeshAnalysisStatus;
    score: number | null;
    tier: "GOOD" | "BETTER" | "BEST" | null;
    isStale: boolean;
    currentSnapshotVersion: number | null;
    analyzedSnapshotVersion: number | null;
}

export interface ContextMeshWorkspace {
    contextMeshId: string;
    name: string;
    description: string | null;
    problemStatement: string | null;
    solutionStatement: string | null;
    personas: string[];
    teamComposition: string | null;
    nonfunctionalRequirements: Record<string, string>;
    metadata: Record<string, unknown>;
    lifecycleStatus: string;
    version: number;
    accessRole: ContextMeshAccessRole;
    createdAt: string | null;
    updatedAt: string | null;
    analysisSummary?: ContextMeshAnalysisSummary;
}

export interface CreateContextMeshRequest {
    name: string;
    description?: string;
    problemStatement?: string;
    solutionStatement?: string;
    personas?: string[];
    teamComposition?: string;
    nonfunctionalRequirements?: Record<string, string>;
    metadata?: Record<string, unknown>;
}

export interface UpdateContextMeshRequest extends Partial<CreateContextMeshRequest> {
    expectedVersion?: number;
}

export interface WorkspaceContextMeshAnalysis {
    contextMeshId: string;
    currentSnapshotVersion: number;
    analyzedSnapshotVersion: number | null;
    status: "pending" | "running" | "ready" | "failed";
    resultSource?: string | null;
    isStale: boolean;
    analysis: null | {
        tier: ContextMeshTier;
        score: number;
        readinessScore: number;
        qualityScore: number;
        categoryScores: ContextMeshCategoryScores | null;
        categoryAnalysis?: unknown;
        rubricVersion?: string;
        recommendations: string[];
        recommendationDetails?: Array<{
            recommendationId?: string;
            code?: string;
            message: string;
            contextCategory?: string;
            priority?: number;
        }>;
    };
}

export interface ContextMeshSource {
    sourceBindingId: string;
    sourceKind: "vector_file" | "git_repository" | "integration";
    provider: string;
    sourceKey: string;
    configuration: Record<string, unknown>;
    bindingStatus: string;
    lastSyncStatus: string;
    lastSyncedAt: string | null;
    vectorFileId: number | null;
    fileName: string | null;
    mimeType: string | null;
    totalTokens: number | null;
    indexedChunks: number | null;
    repositoryId: number | null;
    repositoryName: string | null;
    repositoryUrl: string | null;
    repositoryMetadata: Record<string, unknown>;
    userIntegrationCredentialId: number | null;
    externalRef: string | null;
    externalName: string | null;
    integrationMetadata: Record<string, unknown>;
    syncedCount: number | null;
    syncedUnit: string | null;
    sourceName: string | null;
    sourceReference: string | null;
    branch: string | null;
    lastErrorCode: string | null;
}

export interface ContextMeshProductLink {
    productLinkId: string;
    contextMeshId: string;
    productId: number;
    productName: string | null;
    linkRole: "primary" | "related" | "source";
    includeProductSources: boolean;
    createdAt: string | null;
}

export type CreateContextMeshSourceRequest =
    | { sourceKind: "vector_file"; provider: "internal"; vectorFileId: number; configuration?: Record<string, unknown> }
    | { sourceKind: "git_repository"; provider: string; repositoryId: number; configuration?: Record<string, unknown> }
    | {
          sourceKind: "integration";
          provider: string;
          userIntegrationCredentialId?: number;
          externalRef: string;
          externalName: string;
          integrationMetadata?: Record<string, unknown>;
          configuration?: Record<string, unknown>;
      };

export class ContextMeshApiError extends Error {
    readonly status: number;
    readonly code?: string;

    constructor(message: string, status: number, code?: string) {
        super(message);
        this.name = "ContextMeshApiError";
        this.status = status;
        this.code = code;
    }
}

async function expectJson<T>(response: Response, fallback: string): Promise<T> {
    const payload = await response.json().catch(() => ({})) as {
        error?: { code?: string; message?: string } | string;
    };
    if (!response.ok) {
        const structured = typeof payload.error === "object" ? payload.error : undefined;
        const message = structured?.message || (typeof payload.error === "string" ? payload.error : fallback);
        throw new ContextMeshApiError(message, response.status, structured?.code);
    }
    return payload as T;
}

export async function listContextMeshes(token: string): Promise<ContextMeshWorkspace[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/context-meshes`, token);
    const payload = await expectJson<{ contextMeshes: ContextMeshWorkspace[] }>(response, "Unable to load Context Meshes.");
    return payload.contextMeshes;
}

/** Resolves an exact name first, then a single unambiguous partial name. */
export function resolveContextMeshByName(
    meshes: ContextMeshWorkspace[],
    requestedName: string
): ContextMeshWorkspace | null {
    const normalizedName = requestedName.trim().toLocaleLowerCase();
    if (!normalizedName) return null;
    const exact = meshes.find(mesh => mesh.name.toLocaleLowerCase() === normalizedName);
    if (exact) return exact;
    const partialMatches = meshes.filter(mesh => mesh.name.toLocaleLowerCase().includes(normalizedName));
    return partialMatches.length === 1 ? partialMatches[0] : null;
}

export async function createContextMesh(token: string, request: CreateContextMeshRequest): Promise<ContextMeshWorkspace> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/context-meshes`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
    const payload = await expectJson<{ contextMesh: ContextMeshWorkspace }>(response, "Unable to create the Context Mesh.");
    return payload.contextMesh;
}

export async function getContextMesh(
    token: string,
    contextMeshId: string,
    productId?: number
): Promise<ContextMeshWorkspace> {
    const path = productId == null
        ? `/api/context-meshes/${contextMeshId}`
        : `/api/project/${productId}/context-meshes/${contextMeshId}`;
    const response = await fetchWithAuth(`${API_BASE_URL}${path}`, token);
    const payload = await expectJson<{ contextMesh: ContextMeshWorkspace }>(response, "Unable to load the Context Mesh.");
    return payload.contextMesh;
}

export async function updateContextMesh(
    token: string,
    contextMeshId: string,
    request: UpdateContextMeshRequest
): Promise<ContextMeshWorkspace> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/context-meshes/${contextMeshId}`, token, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
    const payload = await expectJson<{ contextMesh: ContextMeshWorkspace }>(response, "Unable to update the Context Mesh.");
    return payload.contextMesh;
}

export async function getContextMeshAnalysis(
    token: string,
    contextMeshId: string,
    productId?: number
): Promise<WorkspaceContextMeshAnalysis> {
    const path = productId == null
        ? `/api/context-meshes/${contextMeshId}/context-analysis`
        : `/api/project/${productId}/context-meshes/${contextMeshId}/context-analysis`;
    const response = await fetchWithAuth(`${API_BASE_URL}${path}`, token);
    return expectJson<WorkspaceContextMeshAnalysis>(response, "Unable to load Context Mesh analysis.");
}

export async function addContextMeshSource(
    token: string,
    contextMeshId: string,
    request: CreateContextMeshSourceRequest
): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/context-meshes/${contextMeshId}/sources`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
    await expectJson<{ source: unknown }>(response, "Unable to attach Context Mesh context.");
}

export async function listContextMeshSources(token: string, contextMeshId: string): Promise<ContextMeshSource[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/context-meshes/${contextMeshId}/sources`, token);
    const payload = await expectJson<{ sources: ContextMeshSource[] }>(response, "Unable to load Context Mesh sources.");
    return payload.sources;
}

export async function removeContextMeshSource(
    token: string,
    contextMeshId: string,
    sourceBindingId: string
): Promise<void> {
    const response = await fetchWithAuth(
        `${API_BASE_URL}/api/context-meshes/${contextMeshId}/sources/${sourceBindingId}`,
        token,
        { method: "DELETE" }
    );
    if (!response.ok) await expectJson(response, "Unable to remove Context Mesh context.");
}

export async function listContextMeshProductLinks(
    token: string,
    contextMeshId: string
): Promise<ContextMeshProductLink[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/context-meshes/${contextMeshId}/product-links`, token);
    const payload = await expectJson<{ productLinks: ContextMeshProductLink[] }>(response, "Unable to resolve Context Mesh scope.");
    return payload.productLinks;
}

export async function refreshContextMeshAnalysis(
    token: string,
    contextMeshId: string,
    productId?: number
): Promise<void> {
    const path = productId == null
        ? `/api/context-meshes/${contextMeshId}/context-analysis/refresh`
        : `/api/project/${productId}/context-meshes/${contextMeshId}/context-analysis/refresh`;
    const response = await fetchWithAuth(`${API_BASE_URL}${path}`, token, {
        method: "POST"
    });
    await expectJson<Record<string, unknown>>(response, "Unable to refresh Context Mesh analysis.");
}

export async function completeContextMeshCreationDraft(
    token: string,
    contextMeshId: string,
    draftId: number,
    draftVersion: number
): Promise<void> {
    const response = await fetchWithAuth(
        `${API_BASE_URL}/api/context-meshes/${contextMeshId}/creation-draft/complete`,
        token,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ draftId, draftVersion })
        }
    );
    await expectJson<Record<string, unknown>>(
        response,
        "Unable to finalize the canonical Context Mesh v2 score."
    );
}

export async function deleteContextMesh(token: string, contextMeshId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/context-meshes/${contextMeshId}`, token, {
        method: "DELETE"
    });
    if (!response.ok) await expectJson(response, "Unable to delete the Context Mesh.");
}
